import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiPlus, 
  FiX,
  FiCheck,
  FiAlertTriangle,
  FiEye,
  FiFilter,
  FiShield,
  FiUser,
  FiCalendar,
  FiChevronDown,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiInfo,
  FiFileText
} from 'react-icons/fi';
import ConsentService from '../../services/consentService';
import TopHeader from "../../../../common/Header/TopHeader";
import PatientManagement from '../../../../common/Menu/PatientManagement';
import {API_BASE_URL, API_DOCTOR_LIST} from '../../../../../helpers/urlHelper';
import ConsentCreationForm from './ConsentCreationForm'; 

const ConsentTreatment = ({ onCountUpdate, componentName = 'ConsentTreatment' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const patientData = location.state;
  const patientId = patientData?.patientId;

  const [consents, setConsents] = useState([]);
  const [filteredConsents, setFilteredConsents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showCreateOffcanvas, setShowCreateOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [filters, setFilters] = useState({
    status: 'All',
    grantedToType: '',
    durationType: '',
    createdDate: '',
    endDate: ''
  });

  const loadConsents = useCallback(async () => {
    if (!patientId) {
      console.log('No patientId provided');
      setConsents([]);
      onCountUpdate && onCountUpdate(componentName, 0);
      return;
    }
    
    setIsLoading(true);
    try {
      console.log('Loading consents for patient:', patientId);
      const response = await ConsentService.getPatientConsents(patientId);
      console.log('Raw API response:', response);
      
      let consentsData = [];
      
      if (response.data?.consents) {
        consentsData = Array.isArray(response.data.consents) 
          ? response.data.consents 
          : [response.data.consents];
      } else if (Array.isArray(response.data)) {
        consentsData = response.data;
      }

      const normalizedConsents = consentsData.map(consent => {
        const grantedToData = consent.grantedTo?.id || {};
        const doctorName = grantedToData.name || '';
        const doctorSurname = grantedToData.surname || '';
        const fullName = `${doctorName} ${doctorSurname}`.trim() || 'Unknown';

        return {
          consent_id: consent._id,
          patient_id: consent.patient?._id || consent.patient,
          patient_name: consent.patient?.name || patientData?.patientName || 'Unknown',
          granted_to_type: consent.grantedTo?.type || '',
          granted_to_id: consent.grantedTo?.id?._id || consent.grantedTo?.id,
          granted_to_name: doctorName,
          granted_to_surname: doctorSurname,
          granted_to_full_name: fullName,
          granted_to_specialty: grantedToData.specialty || '',
          granted_to_email: grantedToData.email || '',
          status: consent.status || 'active',
          duration_type: consent.duration?.type || 'permanent',
          expires_on: consent.duration?.expiresOn || null,
          purpose: consent.purpose || '',
          module_consents: consent.moduleConsents || [],
          created_at: consent.createdAt,
          approved_at: consent.approvedAt,
          revoked_at: consent.revokedAt,
          is_valid: consent.isCurrentlyValid !== undefined ? consent.isCurrentlyValid : true,
          days_until_expiration: consent.daysUntilExpiration,
          _original: consent
        };
      });

      console.log('Normalized consents:', normalizedConsents);
      
      if (consents.length !== normalizedConsents.length) {
        onCountUpdate && onCountUpdate(componentName, normalizedConsents.length);
      }
      
      setConsents(normalizedConsents);
    } catch (error) {
      console.error('Error loading consents:', error);
      setConsents([]);
      if (consents.length !== 0) {
        onCountUpdate && onCountUpdate(componentName, 0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [patientId, consents.length, onCountUpdate, componentName, patientData]);

  const applyFilters = useCallback(() => {
    let filtered = [...consents];

    if (globalFilter) {
      filtered = filtered.filter(consent => 
        (consent.granted_to_full_name || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (consent.purpose || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (consent.granted_to_type || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (consent.granted_to_specialty || '').toLowerCase().includes(globalFilter.toLowerCase())
      );
    }

    if (filters.status !== 'All') {
      filtered = filtered.filter(consent => consent.status === filters.status.toLowerCase());
    }

    if (filters.grantedToType) {
      filtered = filtered.filter(consent => 
        consent.granted_to_type === filters.grantedToType
      );
    }

    if (filters.durationType) {
      filtered = filtered.filter(consent => 
        consent.duration_type === filters.durationType
      );
    }

    if (filters.createdDate) {
      filtered = filtered.filter(consent => {
        const createdDate = consent.created_at;
        return createdDate && new Date(createdDate) >= new Date(filters.createdDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(consent => {
        const createdDate = consent.created_at;
        return createdDate && new Date(createdDate) <= new Date(filters.endDate);
      });
    }

    filtered.sort((a, b) => {
      const getDate = (item) => new Date(item.created_at || new Date());
      
      switch (sortBy) {
        case 'newest':
          return getDate(b) - getDate(a);
        case 'oldest':
          return getDate(a) - getDate(b);
        case 'expiring':
          if (a.expires_on && b.expires_on) {
            return new Date(a.expires_on) - new Date(b.expires_on);
          }
          return a.expires_on ? -1 : 1;
        case 'alphabetical':
          return (a.granted_to_full_name || '').localeCompare(b.granted_to_full_name || '');
        default:
          return 0;
      }
    });

    setFilteredConsents(filtered);
  }, [consents, filters, globalFilter, sortBy]);

  useEffect(() => {
    if (patientId) {
      loadConsents();
    }
  }, [patientId, loadConsents]);

  useEffect(() => {
    if (consents.length > 0) {
      applyFilters();
    } else {
      setFilteredConsents([]);
    }
  }, [consents, filters, globalFilter, sortBy, applyFilters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showFilterOffcanvas || showCreateOffcanvas || showViewOffcanvas) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '17px';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, [showFilterOffcanvas, showCreateOffcanvas, showViewOffcanvas]);

  const handleViewConsent = (consentId) => {
    const consent = consents.find(c => c.consent_id === consentId);
    if (consent) {
      setSelectedConsent(consent);
      setShowViewOffcanvas(true);
    }
  };

  const handleRevokeConsent = async (consentId) => {
    if (window.confirm('¿Estás seguro de que deseas revocar este consentimiento?')) {
      try {
        setIsSubmitting(true);
        await ConsentService.revokeConsent(consentId, {
          reason: 'Revoked by patient',
          revokedBy: patientId
        });
        await loadConsents();
        alert('Consentimiento revocado exitosamente');
      } catch (error) {
        console.error('Error revoking consent:', error);
        alert('Error al revocar el consentimiento');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCreateConsent = () => {
    setShowCreateOffcanvas(true);
  };

  const handleSubmitConsent = async (consentData) => {
    setIsSubmitting(true);
    try {
      await ConsentService.createConsent(consentData);
      await loadConsents();
      setShowCreateOffcanvas(false);
      alert('Consentimiento creado exitosamente');
    } catch (error) {
      console.error('Error creating consent:', error);
      alert('Error al crear el consentimiento');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSortChange = useCallback((sortOption) => {
    console.log('Cambiando orden a:', sortOption);
    setSortBy(sortOption);
    setShowSortDropdown(false);
  }, []);

  const getSortDisplayText = () => {
    switch (sortBy) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'expiring': return 'Expiring Soon';
      case 'alphabetical': return 'A-Z';
      default: return 'Sort by';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'revoked': return 'danger';
      case 'expired': return 'secondary';
      case 'pending': return 'warning';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status, isValid) => {
    if (!isValid) return <FiXCircle />;
    switch (status?.toLowerCase()) {
      case 'active': return <FiCheckCircle />;
      case 'revoked': return <FiXCircle />;
      case 'expired': return <FiClock />;
      default: return <FiInfo />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const getModuleLabel = (moduleName) => {
    const labels = {
      appointments: 'Appointments',
      prescriptions: 'Prescriptions',
      electronic_prescriptions: 'Electronic Prescriptions',
      laboratory: 'Laboratory Results',
      imaging: 'Imaging Studies',
      molecular: 'Molecular Tests',
      vaccinations: 'Vaccinations',
      treatments: 'Treatments',
      medical_records: 'Medical Records',
      all: 'All Data'
    };
    return labels[moduleName] || moduleName;
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'granted_to_full_name',
      header: 'Granted To',
      size: 220,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div>
            <div className="fw-bold text-primary">
              {row.original.granted_to_name} {row.original.granted_to_surname}
            </div>
            <small className="text-muted d-block">
              <span className={`badge bg-${row.original.granted_to_type === 'doctor' ? 'info' : 'primary'} me-1`}>
                {row.original.granted_to_type}
              </span>
              {row.original.granted_to_specialty && (
                <span>{row.original.granted_to_specialty}</span>
              )}
            </small>
            {row.original.granted_to_email && (
              <small className="text-muted d-block">{row.original.granted_to_email}</small>
            )}
          </div>
        </div>
      )
    },
    {
      accessorKey: 'module_consents',
      header: 'Consented Data',
      size: 250,
      Cell: ({ row }) => {
        const modules = row.original.module_consents || [];
        return (
          <div>
            {modules.length === 0 ? (
              <span className="text-muted">No data</span>
            ) : (
              <div className="d-flex flex-wrap gap-1">
                {modules.slice(0, 3).map((mc, idx) => (
                  <span key={idx} className="badge bg-secondary" style={{fontSize: '0.75rem'}}>
                    <FiFileText className="me-1" size={10} />
                    {getModuleLabel(mc.module)}
                    {mc.specificElements && mc.specificElements.length > 0 && (
                      <span className="ms-1">({mc.specificElements.length})</span>
                    )}
                  </span>
                ))}
                {modules.length > 3 && (
                  <span className="badge bg-dark" style={{fontSize: '0.75rem'}}>
                    +{modules.length - 3} more
                  </span>
                )}
              </div>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'purpose',
      header: 'Purpose',
      size: 180,
      Cell: ({ row }) => (
        <div className="text-wrap" style={{maxWidth: '180px'}}>
          {row.original.purpose || 'Not specified'}
        </div>
      )
    },
    {
      accessorKey: 'duration_type',
      header: 'Duration',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <span className={`badge bg-${row.original.duration_type === 'permanent' ? 'info' : 'warning'}`}>
            {row.original.duration_type}
          </span>
          {row.original.expires_on && (
            <small className="d-block text-muted mt-1">
              {formatDate(row.original.expires_on)}
            </small>
          )}
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      Cell: ({ row }) => (
        <span className={`badge bg-${getStatusColor(row.original.status)} d-flex align-items-center gap-1`}>
          {getStatusIcon(row.original.status, row.original.is_valid)}
          <span className="text-capitalize">{row.original.status}</span>
        </span>
      )
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      size: 120,
      Cell: ({ row }) => (
        <small>{formatDate(row.original.created_at)}</small>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewConsent(row.original.consent_id);
            }}
            title="View Details"
          >
            <FiEye />
          </button>
          {row.original.status === 'active' && (
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                handleRevokeConsent(row.original.consent_id);
              }}
              title="Revoke"
              disabled={isSubmitting}
            >
              <FiXCircle />
            </button>
          )}
        </div>
      )
    }
  ], [consents, isSubmitting]);

  return (
    <main id="app">
      <TopHeader />
      <div className="container-fluid w-100">
        <PatientManagement activeMenu='/consent-management'/>
        
        <div className="mb-3">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
              <li className="breadcrumb-item"><a href="/patients/list">Patients</a></li>
              <li className="breadcrumb-item active" aria-current="page">
                My Consents {patientData?.patientName ? `- ${patientData.patientName}` : ''}
              </li>
            </ol>
          </nav>
        </div>

        <div className="card-body">
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{maxWidth: '300px'}}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search"
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                    />
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <div className="dropdown" style={{position: 'relative'}}>
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSortDropdown(!showSortDropdown);
                      }}
                    >
                      {getSortDisplayText()} <FiChevronDown />
                    </button>
                    {showSortDropdown && (
                      <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', right: 0, zIndex: 1000}}>
                        <button 
                          className={`dropdown-item ${sortBy === 'newest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('newest')}
                        >
                          Newest First
                        </button>
                        <button 
                          className={`dropdown-item ${sortBy === 'oldest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('oldest')}
                        >
                          Oldest First
                        </button>
                        <button 
                          className={`dropdown-item ${sortBy === 'expiring' ? 'active' : ''}`}
                          onClick={() => handleSortChange('expiring')}
                        >
                          Expiring Soon
                        </button>
                        <button 
                          className={`dropdown-item ${sortBy === 'alphabetical' ? 'active' : ''}`}
                          onClick={() => handleSortChange('alphabetical')}
                        >
                          A-Z
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() => setShowFilterOffcanvas(true)}
                  >
                    <FiFilter /> Filter
                  </button>
            
                  <button
                    className="btn btn-primary d-flex align-items-center gap-2"
                    onClick={handleCreateConsent}
                  >
                    <FiPlus /> Add New
                  </button>
                </div>
              </div>
            </div>
          </div>

          <MaterialReactTable
            columns={columns}
            data={filteredConsents}
            enableRowSelection
            enableColumnFilters
            enableSorting={false}
            enablePagination
            initialState={{
              pagination: { pageSize: 10, pageIndex: 0 },
              density: 'comfortable'
            }}
            state={{
              isLoading,
              rowSelection,
              globalFilter
            }}
            onRowSelectionChange={setRowSelection}
            onGlobalFilterChange={setGlobalFilter}
          />

          {filteredConsents.length > 0 && (
            <div className="mt-3">
              <small className="text-muted">
                Showing {filteredConsents.length} of {consents.length} consents
              </small>
            </div>
          )}
        </div>
      </div>

      {showFilterOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={() => setShowFilterOffcanvas(false)}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1">
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filter Consents</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowFilterOffcanvas(false)}
              ></button>
            </div>
            <div className="offcanvas-body">
              <form>
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="All">All</option>
                    <option value="Active">Active</option>
                    <option value="Revoked">Revoked</option>
                    <option value="Expired">Expired</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Granted To Type</label>
                  <select
                    className="form-select"
                    value={filters.grantedToType}
                    onChange={(e) => setFilters(prev => ({ ...prev, grantedToType: e.target.value }))}
                  >
                    <option value="">All Types</option>
                    <option value="doctor">Doctor</option>
                    <option value="center">Center</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Duration Type</label>
                  <select
                    className="form-select"
                    value={filters.durationType}
                    onChange={(e) => setFilters(prev => ({ ...prev, durationType: e.target.value }))}
                  >
                    <option value="">All Durations</option>
                    <option value="permanent">Permanent</option>
                    <option value="temporary">Temporary</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Created From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.createdDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, createdDate: e.target.value }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Created To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary flex-fill"
                    onClick={() => setShowFilterOffcanvas(false)}
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={() => setFilters({
                      status: 'All',
                      grantedToType: '',
                      durationType: '',
                      createdDate: '',
                      endDate: ''
                    })}
                  >
                    Clear All
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      <ConsentCreationForm
        isOpen={showCreateOffcanvas}
        onClose={() => setShowCreateOffcanvas(false)}
        patientId={patientId}
        patientData={patientData}
        onSuccess={async () => {
          await loadConsents();
          setShowCreateOffcanvas(false);
        }}
      />

      {showViewOffcanvas && selectedConsent && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={() => setShowViewOffcanvas(false)}></div>
          <div className="offcanvas offcanvas-end show" style={{width: '650px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Consent Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowViewOffcanvas(false)}
              ></button>
            </div>
            <div className="offcanvas-body">
              <ViewConsentDetails 
                consent={selectedConsent} 
                onRevoke={handleRevokeConsent}
                getModuleLabel={getModuleLabel}
              />
            </div>
          </div>
        </>
      )}
    </main>
  );
};

const ViewConsentDetails = ({ consent, onRevoke, getModuleLabel }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  return (
    <div className="row g-3">
      <div className="col-12">
        {consent.status === 'active' && consent.is_valid && (
          <div className="alert alert-success">
            <FiCheckCircle className="me-2" />
            This consent is currently active and valid
          </div>
        )}
        {consent.status === 'active' && !consent.is_valid && (
          <div className="alert alert-warning">
            <FiClock className="me-2" />
            This consent has expired
          </div>
        )}
        {consent.status === 'revoked' && (
          <div className="alert alert-danger">
            <FiXCircle className="me-2" />
            This consent has been revoked
          </div>
        )}
      </div>

      <div className="col-12">
        <div className="card">
          <div className="card-header bg-primary text-white">
            <h6 className="mb-0"><FiUser className="me-2" />Recipient Information</h6>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold small text-muted">Name</label>
                <p className="mb-0">
                  {consent.granted_to_name} {consent.granted_to_surname}
                </p>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold small text-muted">Type</label>
                <p className="mb-0">
                  <span className={`badge bg-${consent.granted_to_type === 'doctor' ? 'info' : 'primary'}`}>
                    {consent.granted_to_type}
                  </span>
                </p>
              </div>
              {consent.granted_to_specialty && (
                <div className="col-md-6">
                  <label className="form-label fw-bold small text-muted">Specialty</label>
                  <p className="mb-0">{consent.granted_to_specialty}</p>
                </div>
              )}
              {consent.granted_to_email && (
                <div className="col-md-6">
                  <label className="form-label fw-bold small text-muted">Email</label>
                  <p className="mb-0">{consent.granted_to_email}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="card">
          <div className="card-header bg-info text-white">
            <h6 className="mb-0"><FiFileText className="me-2" />Authorized Data Categories</h6>
          </div>
          <div className="card-body">
            {!consent.module_consents || consent.module_consents.length === 0 ? (
              <p className="text-muted mb-0">No data categories authorized</p>
            ) : (
              <div className="row g-2">
                {consent.module_consents.map((moduleConsent, index) => (
                  <div key={index} className="col-12">
                    <div className="border rounded p-3 bg-light">
                      <div className="d-flex align-items-start justify-content-between">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <FiCheckCircle className="text-success" />
                            <span className="fw-bold">{getModuleLabel(moduleConsent.module)}</span>
                            {moduleConsent.fullConsent && (
                              <span className="badge bg-success" style={{fontSize: '0.7rem'}}>Full Access</span>
                            )}
                          </div>
                          
                          {moduleConsent.allowedActions && moduleConsent.allowedActions.length > 0 && (
                            <div className="mb-2">
                              <small className="text-muted d-block mb-1">Allowed Actions:</small>
                              <div className="d-flex gap-1">
                                {moduleConsent.allowedActions.map((action, i) => (
                                  <span key={i} className="badge bg-secondary" style={{fontSize: '0.7rem'}}>
                                    {action}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {moduleConsent.specificElements && moduleConsent.specificElements.length > 0 && (
                            <div>
                              <small className="text-muted d-block mb-2">
                                Specific Items ({moduleConsent.specificElements.length}):
                              </small>
                              <div className="ms-3">
                                {moduleConsent.specificElements.slice(0, 5).map((element, i) => (
                                  <div key={i} className="mb-1 small">
                                    <FiFileText className="me-1" size={12} />
                                    <span className="text-muted">
                                      {element.elementType}: {element.metadata?.description || element.elementId}
                                      {element.metadata?.date && (
                                        <span className="ms-2">
                                          ({new Date(element.metadata.date).toLocaleDateString()})
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                ))}
                                {moduleConsent.specificElements.length > 5 && (
                                  <small className="text-muted">
                                    ... and {moduleConsent.specificElements.length - 5} more items
                                  </small>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="card">
          <div className="card-header bg-warning text-dark">
            <h6 className="mb-0"><FiCalendar className="me-2" />Duration & Important Dates</h6>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold small text-muted">Duration Type</label>
                <p className="mb-0">
                  <span className={`badge bg-${consent.duration_type === 'permanent' ? 'info' : 'warning'}`}>
                    {consent.duration_type}
                  </span>
                </p>
              </div>
              {consent.expires_on && (
                <div className="col-md-6">
                  <label className="form-label fw-bold small text-muted">Expires On</label>
                  <p className="mb-0">
                    {formatDate(consent.expires_on)}
                    {consent.days_until_expiration !== null && consent.days_until_expiration > 0 && (
                      <small className="d-block text-warning">
                        {consent.days_until_expiration} days remaining
                      </small>
                    )}
                  </p>
                </div>
              )}
              <div className="col-md-6">
                <label className="form-label fw-bold small text-muted">Created</label>
                <p className="mb-0">{formatDate(consent.created_at)}</p>
              </div>
              {consent.approved_at && (
                <div className="col-md-6">
                  <label className="form-label fw-bold small text-muted">Approved</label>
                  <p className="mb-0">{formatDate(consent.approved_at)}</p>
                </div>
              )}
              {consent.revoked_at && (
                <div className="col-md-6">
                  <label className="form-label fw-bold small text-muted">Revoked</label>
                  <p className="mb-0 text-danger">{formatDate(consent.revoked_at)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="card">
          <div className="card-header bg-secondary text-white">
            <h6 className="mb-0"><FiInfo className="me-2" />Purpose & Additional Information</h6>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label fw-bold small text-muted">Purpose</label>
                <p className="mb-0">{consent.purpose || 'Not specified'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {consent.status === 'active' && consent.is_valid && (
        <div className="col-12">
          <button
            className="btn btn-danger w-100"
            onClick={() => onRevoke(consent.consent_id)}
          >
            <FiXCircle className="me-2" />
            Revoke This Consent
          </button>
        </div>
      )}
    </div>
  );
};

export default ConsentTreatment;